import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { logout } from "./authSlice";
import type { RootState } from "./index";

// --------------------- types (minimal, expand as needed) ---------------------

export interface MarketRow {
  symbol: string;
  price: number;
  change_pct_24h: number;
  rsi_14: number;
  sma_20: number;
  sma_50: number;
}

export interface BackendStatus {
  settings?: Record<string, unknown>;
  balances?: Record<string, number>;
  open_positions_usd?: Record<string, number>;
  free_quote: number;
  position_value_usd: number;
  markets: MarketRow[];
  trades_today: number;
  recent_cycles?: BackendCycle[];
  stop_file_present?: boolean;
  // Convenience accessors used by the UI; mirror dry_run/paused/approval_mode
  // from settings so consumers don't have to drill in.
  paused?: boolean;
  approval_mode?: "manual" | "auto";
  dry_run?: boolean;
}

export interface BackendCycle {
  id: number;
  started_at: string;
  finished_at?: string | null;
  status: string;
  quote_free?: number | null;
  position_value_usd?: number | null;
  notes?: string | null;
  balances_json?: string | null;
  // Owner attribution: present when the cycle was a single-user per-user
  // run (one user_cycles row). Admin/global cycles leave both NULL.
  user_id?: number | null;
  user_email?: string | null;
  // Latest decision attached to this cycle (deep-advisor override wins
  // when present). NULL when the cycle never produced a decision row.
  decision_action?: "BUY" | "SELL" | "HOLD" | null;
  decision_symbol?: string | null;
  decision_size_usd?: number | null;
}

export interface MyCycleRow {
  id: number;
  parent_cycle_id: number;
  user_id: number;
  started_at: string;
  finished_at: string | null;
  status: "executed" | "queued" | "held" | "skipped" | "error" | string;
  action: "BUY" | "SELL" | "HOLD" | null;
  symbol: string | null;
  proposed_size_usd: number | null;
  applied_size_usd: number | null;
  notes: string | null;
  pending_id: string | null;
  trade_order_id: string | null;
  error: string | null;
  personal_review_action: string | null;
  personal_review_size_usd: number | null;
  personal_review_reasoning: string | null;
  personal_review_model: string | null;
  cycle_started_at: string;
  cycle_finished_at: string | null;
  cycle_status: string;
  cycle_notes?: string | null;
}

export interface BackendCycleStep {
  id: number;
  cycle_id: number;
  step_order: number;
  name: string;
  status: string;
  started_at: string;
  finished_at?: string | null;
  summary?: string | null;
  detail_json?: string | null;
}

export interface ScalpStatus {
  enabled: boolean;
  paused: boolean;
  strategy: string;
  use_llm_gatekeeper: boolean;
  open_positions_count: number;
  trades_today: number;
  max_concurrent_positions: number;
  max_trades_per_day: number;
  loop_seconds: number;
  last_tick?: ScalpTick | null;
}

export interface ScalpCandidate {
  symbol?: string;
  pipeline?: "quant" | "legacy";
  regime?: string;
  atr_pct?: number;
  price?: number;
  rsi?: number;
  sma20?: number;
  change_24h_pct?: number;
  had_signal?: boolean;
  strategy?: string;
  reason?: string;
  size_usd?: number;
  risk_size_usd?: number;
  expected_target_pct?: number;
  expected_stop_pct?: number;
  spread_pct?: number;
  volume_24h_usd?: number;
  range_24h_pct?: number;
  gatekeeper_status?: string;
  gatekeeper_confidence?: number;
  gatekeeper_reason?: string;
  risk_guard_reason?: string;
  required_pct?: number;
  filter_trend?: string;
  filter_volatility?: string;
  filter_liquidity?: string;
  filter_spread?: string;
  llm_target_pct?: number;
  llm_stop_pct?: number;
  open?: number;
  cap?: number;
  today?: number;
  free_quote?: number;
}

export interface ScalpTick {
  ts: string;
  status: string;
  candidates?: ScalpCandidate[];
  opened?: number;
  closed?: number;
  cooldown_seconds_remaining?: number;
}

export interface ScalpPosition {
  id: number;
  symbol: string;
  side: string;
  entry_price: number;
  entry_time: string;
  qty: number;
  target_price: number;
  stop_price: number;
  status: string;
  exit_price?: number | null;
  exit_time?: string | null;
  fees_paid?: number | null;
  net_pnl_usd?: number | null;
  strategy?: string | null;
  exit_state?: string | null;
  high_water?: number | null;
  partial_exits_json?: string | null;
  economics?: {
    current_price?: number;
    unrealized_pnl_net?: number;
    unrealized_pct?: number;
    pct_to_target?: number;
    pct_to_stop?: number;
    seconds_until_timeout?: number | null;
    recommendation?: string;
    "$_to_target"?: number;
    "$_to_stop"?: number;
  };
}

export interface ScalpMetrics {
  window: number;
  insufficient_data?: boolean;
  min_required?: number;
  win_rate?: number;
  expectancy?: number;
  avg_win?: number;
  avg_loss?: number;
  sharpe?: number;
  max_drawdown?: number;
  total_pnl?: number;
  auto_pause_armed?: boolean;
  auto_pause_window?: number;
  auto_pause_tripped?: boolean;
  consecutive_losses?: number;
  loss_cooldown_seconds_remaining?: number | null;
}

export interface PendingTrade {
  id: string;
  decision_id?: number;
  created_at: string;
  status: string;
  resolved_at?: string | null;
  action: "BUY" | "SELL";
  symbol: string;
  size_usd: number;
  confidence?: number | null;
  reasoning?: string | null;
  snapshot_price?: number | null;
  error?: string | null;
  order_id?: string | null;
}

export interface BackendTrade {
  id: number;
  pending_id?: string | null;
  decision_id?: number | null;
  executed_at: string;
  action: string;
  symbol: string;
  size_usd?: number | null;
  filled_qty?: number | null;
  avg_price?: number | null;
  fee?: number | null;
  fee_currency?: string | null;
  order_id?: string | null;
  dry_run: number;
  source?: string | null;
  raw_response?: string | null;
  realized_pnl_usd?: number | null;
  user_id?: number | null;
  user_email?: string | null;
  // Populated only for DCA buys via /api/trades (live ticker join).
  unrealized_pnl_usd?: number | null;
  unrealized_pnl_pct?: number | null;
  last_price?: number | null;
}

export interface BackendDecision {
  id: number;
  cycle_id: number;
  decided_at: string;
  action: "BUY" | "SELL" | "HOLD";
  symbol?: string | null;
  size_usd?: number | null;
  confidence?: number | null;
  reasoning?: string | null;
  raw_response?: string | null;
}

export type SettingsPatch = Record<string, unknown>;
export type Settings = Record<string, unknown>;

// --------------------- baseQuery with auth + 401 handling --------------------

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
  async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status === 401) {
      api.dispatch(logout());
    }
    return result;
  };

// --------------------- endpoints ---------------------------------------------

export const api = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: [
    "Settings",
    "Status",
    "Cycles",
    "CycleSteps",
    "Pending",
    "Trades",
    "Decisions",
    "Gatekeeper",
    "Tuning",
    "PortfolioPnL",
    "Presets",
    "Watchlist",
    "Narrative",
    "PostTradeReview",
    "LlmCost",
    "SessionPlan",
    "Controller",
    "Ohlcv",
    "Logs",
    "HourlyPositions",
    // Multi-user scalp (Phase D)
    "ScalpAdminStatus",
    "ScalpAdminUsers",
    "WalletReconcile",
    // V5
    "LeadLag",
    "PortfolioWeights",
    "ArbAttempts",
    "NetFlows",
    "Sentiment",
    // Audit agent
    "AuditReports",
    "AuditProposals",
    // News watcher
    "NewsEvents",
  ],
  endpoints: (builder) => ({
    // ---- public ----
    getHealth: builder.query<{ ok: boolean }, void>({
      query: () => "/api/health",
    }),
    login: builder.mutation<
      { token: string; user: { id: number; email: string; role: "admin" | "user" } },
      { email: string; password: string }
    >({
      query: (body) => ({ url: "/api/auth/login", method: "POST", body }),
    }),
    signup: builder.mutation<
      { verification_required: true; email: string },
      { email: string; password: string; accepted_terms: boolean }
    >({
      query: (body) => ({ url: "/api/auth/signup", method: "POST", body }),
    }),
    verifyEmail: builder.mutation<
      { token: string; user: { id: number; email: string; role: "admin" | "user" } },
      { token: string }
    >({
      query: (body) => ({ url: "/api/auth/verify-email", method: "POST", body }),
    }),
    resendVerification: builder.mutation<
      { ok: boolean; message: string },
      { email: string }
    >({
      query: (body) => ({ url: "/api/auth/resend-verification", method: "POST", body }),
    }),
    forgotPassword: builder.mutation<{ ok: boolean; message: string }, { email: string }>({
      query: (body) => ({ url: "/api/auth/forgot", method: "POST", body }),
    }),
    resetPassword: builder.mutation<{ ok: boolean }, { token: string; password: string }>({
      query: (body) => ({ url: "/api/auth/reset", method: "POST", body }),
    }),
    getTerms: builder.query<{ markdown: string; updated_at: string | null }, void>({
      query: () => "/api/terms",
    }),
    getAuthMe: builder.query<
      { id: number; email: string; role: "admin" | "user"; status: string },
      void
    >({
      query: () => "/api/auth/me",
    }),
    // ---- per-user (/api/me) ----
    getMyStatus: builder.query<
      {
        user: { id: number; email: string; role: "admin" | "user" };
        settings: {
          per_order_usd: number; max_orders_per_day: number;
          approval_mode: "manual" | "auto"; paused: boolean;
          starting_equity_usd: number | null;
          personalize_risk_review?: boolean;
          dry_run?: boolean;
        };
        has_keys: boolean;
        free_quote_usdt: number | null;
        trades_today: number;
        users_can_run_cycle?: boolean;
        next_cycle_at?: string | null;
      },
      void
    >({
      query: () => "/api/me/status",
      providesTags: ["MeStatus" as never],
    }),
    getMySettings: builder.query<Record<string, unknown>, void>({
      query: () => "/api/me/settings",
      providesTags: ["MeSettings" as never],
    }),
    getMyMicroHarvest: builder.query<{
      enabled?: boolean;
      realized_pnl_usd: number;
      fills: number;
      state: Array<{ symbol: string; state: string; entry_price: number | null;
        qty: number | null; tp_price: number | null; last_sell_price: number | null;
        realized_pnl_usd: number | null; cycles: number | null }>;
    }, void>({
      query: () => "/api/me/micro-harvest",
    }),
    patchMySettings: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (patch) => ({ url: "/api/me/settings", method: "PATCH", body: patch }),
      invalidatesTags: ["MeSettings" as never, "MeStatus" as never],
    }),
    putMyBinanceKeys: builder.mutation<
      { ok: boolean; cycle_triggered?: boolean; cycle_id?: number | null; next_cycle_at?: string | null },
      { api_key: string; api_secret: string }
    >({
      query: (body) => ({ url: "/api/me/binance-keys", method: "PUT", body }),
      invalidatesTags: ["MeSettings" as never, "MeStatus" as never],
    }),
    changeMyPassword: builder.mutation<
      { ok: boolean },
      { current_password: string; new_password: string }
    >({
      query: (body) => ({ url: "/api/me/password", method: "POST", body }),
    }),
    deleteMyBinanceKeys: builder.mutation<{ ok: boolean }, void>({
      query: () => ({ url: "/api/me/binance-keys", method: "DELETE" }),
      invalidatesTags: ["MeSettings" as never, "MeStatus" as never],
    }),
    // ---- Scalp (independent feature) ----
    // Admin-side scalp
    getGridClosedCycles: builder.query<any[], number | void>({
      query: (limit) => `/api/grid/closed-cycles?limit=${limit ?? 500}`,
    }),
    getProfitTimeseries: builder.query<any, number | void>({
      query: (days) => `/api/profit/timeseries?days=${days ?? 30}`,
    }),
    getLegacyPnl: builder.query<{
      available: boolean;
      summary: {
        total_deposited_usd: number;
        total_withdrawn_usd: number;
        net_funded_in_usd: number;
        net_funded_in_pkr: number;
        peak_balance_usd: number | null;
        peak_balance_date: string | null;
        last_balance_usd: number | null;
        last_balance_date: string | null;
        final_value_usd: number;
        realised_pnl_usd: number;
        flow_count: number;
        balance_count: number;
      };
      balance_history: Array<{ bal_date: string; balance_usd: number; rate: number | null; rupees: number | null }>;
      funding_flows: Array<{ flow_date: string; rupees: number; rate: number | null; usdt: number; direction: string }>;
    }, void>({
      query: () => `/api/legacy/pnl`,
    }),
    getWalletReconcile: builder.query<{
      user_id: number;
      assets: Array<{
        asset: string;
        symbol: string;
        free: number;
        used: number;
        total: number;
        usd_value: number | null;
        tracked_qty: number;
        untracked_qty: number;
        status: "tracked" | "partial" | "orphan" | "untracked" | "dust";
        tracked_hourly_count: number;
        tracked_scalp_count: number;
        unprotected_hourly_pids: number[];
        hourly_pids: number[];
        scalp_pids: number[];
      }>;
    }, { user_id?: number } | void>({
      query: (q) => `/api/admin/wallet-reconcile?user_id=${(q && q.user_id) || 1}`,
      providesTags: ["WalletReconcile" as never],
    }),
    walletReconcileSell: builder.mutation<unknown,
      { user_id: number; asset: string; qty?: number | null }>({
      query: (body) => ({ url: "/api/admin/wallet-reconcile/sell", method: "POST", body }),
      invalidatesTags: ["WalletReconcile" as never],
    }),
    hourlyAttachOco: builder.mutation<{
      pid: number; status: string;
      order_list_id?: string; tp_order_id?: string; sl_order_id?: string;
      reason?: string;
    }, { pid: number }>({
      query: ({ pid }) => ({
        url: `/api/hourly/migrate-to-oco?pid=${pid}`,
        method: "POST",
      }),
      invalidatesTags: ["WalletReconcile" as never, "HourlyPositions" as never],
    }),
    getBtcDominance: builder.query<{
      snapshot: {
        btc_dominance_pct: number;
        fetched_at: string;
        btc_dominance_24h_change_pct: number | null;
        btc_dominance_anchor_fetched_at: string | null;
        samples_24h: number | null;
      } | null;
      enabled: boolean;
      block_threshold_24h_pct_points: number;
    }, void>({
      query: () => "/api/btc-dominance",
    }),
    getMyPending: builder.query<unknown[], { status?: string; limit?: number } | void>({
      query: (q) => {
        const p = new URLSearchParams();
        const qq = q as { status?: string; limit?: number } | undefined;
        if (qq?.status) p.set("status", qq.status);
        if (qq?.limit) p.set("limit", String(qq.limit));
        const s = p.toString();
        return s ? `/api/me/pending?${s}` : "/api/me/pending";
      },
      providesTags: ["MyPending" as never],
    }),
    approveMyPending: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/api/me/pending/${id}/approve`, method: "POST" }),
      invalidatesTags: ["MyPending" as never, "MyTrades" as never, "MeStatus" as never],
    }),
    rejectMyPending: builder.mutation<unknown, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/api/me/pending/${id}/reject`, method: "POST", body: { reason },
      }),
      invalidatesTags: ["MyPending" as never],
    }),
    getMyTrades: builder.query<unknown[], { limit?: number } | void>({
      query: (q) => {
        const qq = q as { limit?: number } | undefined;
        return qq?.limit ? `/api/me/trades?limit=${qq.limit}` : "/api/me/trades";
      },
      providesTags: ["MyTrades" as never],
    }),
    getMyPositions: builder.query<
      any[],
      { status?: "open" | "closed"; limit?: number } | void
    >({
      query: (args) => {
        const status = (args as any)?.status ?? "closed";
        const limit = (args as any)?.limit ?? 200;
        return `/api/me/positions?status=${status}&limit=${limit}`;
      },
      providesTags: ["MyPositions" as never],
    }),
    getPublicCyclesStats: builder.query<any[], number | void>({
      query: (limit) =>
        `/api/public/cycles-stats?limit=${limit ?? 200}`,
    }),
    getMyCycles: builder.query<MyCycleRow[], number | void>({
      query: (limit) => `/api/me/cycles?limit=${limit ?? 100}`,
      providesTags: ["MyCycles" as never],
    }),
    getMyHourlyMetrics: builder.query<
      ScalpMetrics & { consecutive_losses?: number },
      number | void
    >({
      query: (window) =>
        `/api/me/hourly/metrics?window=${window ?? 20}`,
      providesTags: ["MyPositions" as never],
    }),
    getMyCycleDetail: builder.query<
      { row: MyCycleRow; steps: BackendCycleStep[] },
      number
    >({
      query: (id) => `/api/me/cycles/${id}`,
      providesTags: ["MyCycles" as never],
    }),
    runMyCycle: builder.mutation<
      { cycle_id: number; next_cycle_at: string | null },
      void
    >({
      query: () => ({ url: "/api/me/run-cycle", method: "POST" }),
      invalidatesTags: ["MyCycles" as never, "MyStatus" as never],
    }),
    getMyROICurve: builder.query<
      { t: string | null; equity: number; roi_pct: number }[],
      number | void
    >({
      query: (limit) => (limit ? `/api/me/roi-curve?limit=${limit}` : "/api/me/roi-curve"),
      providesTags: ["MyROI" as never],
    }),
    getMyFees: builder.query<
      {
        summary: {
          total_fee: number; paid_fee: number; outstanding_fee: number;
          n_periods: number; lifetime_net_pnl_usd: number;
          // Legacy compat: old builds expected n_accruals here.
          n_accruals?: number;
        };
        periods: Array<{
          id: number; period_ym: string; net_pnl_usd: number; fee_usd: number;
          computed_at: string; paid_at: string | null;
        }>;
        accruals: Array<{
          id: number; accrued_at: string; realized_pnl_usd: number;
          fee_usd: number; paid: number; notes: string | null;
        }>;
        rate_pct: number;
        model: string;
        // Legacy alias for older builds.
        rows?: Array<unknown>;
      },
      void
    >({
      query: () => "/api/me/fees",
      providesTags: ["MyFees" as never],
    }),
    // ---- admin (/api/admin) ----
    getAdminUsers: builder.query<
      Array<{
        id: number; email: string; role: string; status: string;
        created_at: string; has_keys: boolean; trades_today: number;
        settings: { per_order_usd: number; max_orders_per_day: number; paused: boolean };
        fees: { total_fee: number; paid_fee: number; outstanding_fee: number; n_accruals: number };
      }>,
      void
    >({
      query: () => "/api/admin/users",
      providesTags: ["AdminUsers" as never],
    }),
    setAdminUserStatus: builder.mutation<{ ok: boolean }, { id: number; status: "active" | "disabled" }>({
      query: ({ id, status }) => ({
        url: `/api/admin/users/${id}/${status === "active" ? "enable" : "disable"}`,
        method: "POST",
      }),
      invalidatesTags: ["AdminUsers" as never],
    }),
    getAdminUserDetail: builder.query<
      {
        user: { id: number; email: string; role: string; status: string; created_at: string };
        settings: {
          per_order_usd: number; max_orders_per_day: number;
          approval_mode: "manual" | "auto"; paused: boolean;
          starting_equity_usd: number | null;
          personalize_risk_review?: boolean;
          can_run_cycle?: boolean;
          dry_run?: boolean;
          per_user_cycle_enabled?: boolean;
        };
        has_keys: boolean;
        fees: { total_fee: number; paid_fee: number; outstanding_fee: number; n_accruals: number };
        trades_today: number;
        trades: Array<{
          id: number; executed_at: string; action: string; symbol: string;
          size_usd: number; filled_qty: number; avg_price: number;
          fee: number | null; fee_currency: string | null; order_id: string | null;
          dry_run: number; source: string | null;
        }>;
        cycles: MyCycleRow[];
      },
      number
    >({
      query: (uid) => `/api/admin/users/${uid}`,
      providesTags: (_r, _e, uid) => [{ type: "AdminUserDetail" as never, id: uid }],
    }),
    patchAdminUserSettings: builder.mutation<
      Record<string, unknown>,
      { id: number; patch: Record<string, unknown> }
    >({
      query: ({ id, patch }) => ({
        url: `/api/admin/users/${id}/settings`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminUserDetail" as never, id },
        "AdminUsers" as never,
      ],
    }),
    refanoutAdminUser: builder.mutation<
      { ok: boolean; user_id: number; parent_cycle_id: number; outcome: Record<string, unknown> },
      number
    >({
      query: (id) => ({
        url: `/api/admin/users/${id}/refanout-latest`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AdminUserDetail" as never, id },
        "AdminUsers" as never,
      ],
    }),
    loginAsAdminUser: builder.mutation<
      { token: string; user: { id: number; email: string; role: "admin" | "user" } },
      number
    >({
      query: (id) => ({
        url: `/api/admin/users/${id}/login-as`,
        method: "POST",
      }),
    }),
    getAdminFees: builder.query<
      {
        periods: Array<{
          id: number; user_id: number; email: string | null;
          period_ym: string; net_pnl_usd: number; fee_usd: number;
          computed_at: string; paid_at: string | null;
          paid_admin_user_id: number | null; notes: string | null;
        }>;
        accruals: Array<{
          id: number; user_id: number; email: string | null;
          accrued_at: string; realized_pnl_usd: number; fee_usd: number;
          paid: number; notes: string | null;
        }>;
        rate_pct: number;
        model: string;
        // Legacy alias for older builds.
        rows?: Array<unknown>;
      },
      void
    >({
      query: () => "/api/admin/fees",
      providesTags: ["AdminFees" as never],
    }),
    markAdminFeePeriodPaid: builder.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/api/admin/fee-periods/${id}/mark-paid`, method: "POST" }),
      invalidatesTags: ["AdminFees" as never, "AdminUsers" as never],
    }),
    markAdminFeePaid: builder.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/api/admin/fees/${id}/mark-paid`, method: "POST" }),
      invalidatesTags: ["AdminFees" as never, "AdminUsers" as never],
    }),

    // ---- main bot ----
    getStatus: builder.query<BackendStatus, void>({
      query: () => "/api/status",
      providesTags: ["Status"],
    }),
    getSettings: builder.query<Settings, void>({
      query: () => "/api/settings",
      providesTags: ["Settings"],
    }),
    patchSettings: builder.mutation<Settings, SettingsPatch>({
      query: (patch) => ({
        url: "/api/settings",
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: ["Settings", "Status", ],
    }),
    // Force the backend's in-memory settings singleton to re-read the DB.
    // Use after scripts/external tools write the `settings` table directly.
    reloadSettings: builder.mutation<{ status: string; settings: Settings }, void>({
      query: () => ({
        url: "/api/settings/reload",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Settings", "Status", ],
    }),

    // ---- cycles ----
    runCycle: builder.mutation<unknown, void>({
      query: () => ({ url: "/api/cycle/run", method: "POST", body: {} }),
      invalidatesTags: ["Cycles", "Status", "Decisions", "Pending", "Trades"],
    }),
    runCycleAsync: builder.mutation<{ cycle_id: number }, void>({
      query: () => ({ url: "/api/cycle/run-async", method: "POST", body: {} }),
      invalidatesTags: ["Cycles"],
    }),
    getCycles: builder.query<BackendCycle[], void>({
      query: () => "/api/cycles",
      providesTags: ["Cycles"],
    }),
    getCycleSteps: builder.query<BackendCycleStep[], number>({
      query: (cycleId) => `/api/cycles/${cycleId}/steps`,
      providesTags: (_r, _e, id) => [{ type: "CycleSteps", id }],
    }),

    // ---- start / stop main bot ----
    startBot: builder.mutation<Settings, void>({
      query: () => ({ url: "/api/control/start", method: "POST", body: {} }),
      invalidatesTags: ["Settings", "Status"],
    }),
    stopBot: builder.mutation<Settings, void>({
      query: () => ({ url: "/api/control/stop", method: "POST", body: {} }),
      invalidatesTags: ["Settings", "Status"],
    }),

    // ---- decisions / trades / pending ----
    getDecisions: builder.query<BackendDecision[], void>({
      query: () => "/api/decisions",
      providesTags: ["Decisions"],
    }),
    getTrades: builder.query<BackendTrade[], void>({
      query: () => "/api/trades",
      providesTags: ["Trades"],
    }),
    getPending: builder.query<PendingTrade[], string | void>({
      query: (status) =>
        status ? `/api/pending?status=${encodeURIComponent(status)}` : "/api/pending",
      providesTags: ["Pending"],
    }),
    approvePending: builder.mutation<PendingTrade, string>({
      query: (pid) => ({
        url: `/api/pending/${pid}/approve`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Pending", "Trades", "Status"],
    }),
    rejectPending: builder.mutation<PendingTrade, { pid: string; reason?: string }>({
      query: ({ pid, reason }) => ({
        url: `/api/pending/${pid}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["Pending"],
    }),
    cancelAllPending: builder.mutation<unknown, void>({
      query: () => ({
        url: "/api/pending/cancel-all",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Pending"],
    }),

    // ---- scalper ----

    // ---- gatekeeper ----
    getGatekeeperStats: builder.query<unknown, void>({
      query: () => "/api/scalp/gatekeeper/stats",
      providesTags: ["Gatekeeper"],
    }),
    getGatekeeperRecent: builder.query<unknown[], number | void>({
      query: (limit) =>
        limit
          ? `/api/scalp/gatekeeper/recent?limit=${limit}`
          : "/api/scalp/gatekeeper/recent",
      providesTags: ["Gatekeeper"],
    }),

    // ---- tuner ----
    runTunerNow: builder.mutation<unknown, void>({
      query: () => ({
        url: "/api/scalp/tuning/run-now",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Tuning", "Settings"],
    }),
    getTuningRecent: builder.query<unknown[], number | void>({
      query: (limit) =>
        limit
          ? `/api/scalp/tuning/recent?limit=${limit}`
          : "/api/scalp/tuning/recent",
      providesTags: ["Tuning"],
    }),

    // ---- watchlist agent ----
    getWatchlistCurrent: builder.query<any, void>({
      query: () => "/api/scalp/watchlist/current",
      providesTags: ["Watchlist"],
    }),
    getWatchlistRecent: builder.query<any[], number | void>({
      query: (limit) =>
        limit
          ? `/api/scalp/watchlist/recent?limit=${limit}`
          : "/api/scalp/watchlist/recent",
      providesTags: ["Watchlist"],
    }),
    runWatchlistNow: builder.mutation<unknown, void>({
      query: () => ({
        url: "/api/scalp/watchlist/run-now",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Watchlist"],
    }),

    // ---- no-fire narrator ----
    getNarrativeCurrent: builder.query<any, void>({
      query: () => "/api/scalp/narrative/current",
      providesTags: ["Narrative"],
    }),
    getNarrativeRecent: builder.query<any[], number | void>({
      query: (limit) =>
        limit ? `/api/scalp/narrative/recent?limit=${limit}` : "/api/scalp/narrative/recent",
      providesTags: ["Narrative"],
    }),
    runNarrativeNow: builder.mutation<unknown, void>({
      query: () => ({
        url: "/api/scalp/narrative/run-now",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Narrative"],
    }),

    // ---- post-trade reviews ----
    getPostTradeReview: builder.query<any, number>({
      query: (id) => `/api/scalp/positions/${id}/review`,
      providesTags: ["PostTradeReview"],
    }),
    getPostTradeReviewsRecent: builder.query<any[], number | void>({
      query: (limit) =>
        limit ? `/api/scalp/reviews/recent?limit=${limit}` : "/api/scalp/reviews/recent",
      providesTags: ["PostTradeReview"],
    }),

    // ---- session planner ----
    getSessionPlanCurrent: builder.query<any, void>({
      query: () => "/api/scalp/session-plan/current",
      providesTags: ["SessionPlan"],
    }),
    getSessionPlanRecent: builder.query<any[], number | void>({
      query: (limit) =>
        limit ? `/api/scalp/session-plan/recent?limit=${limit}` : "/api/scalp/session-plan/recent",
      providesTags: ["SessionPlan"],
    }),
    runSessionPlanNow: builder.mutation<unknown, void>({
      query: () => ({
        url: "/api/scalp/session-plan/run-now",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["SessionPlan"],
    }),
    getControllerState: builder.query<any, void>({
      query: () => "/api/scalp/controller/state",
      providesTags: ["Controller"],
    }),

    // ---- hourly auto-exits ----
    getHourlyPositions: builder.query<any[], { status?: "open" | "closed"; limit?: number } | void>({
      query: (args) => {
        const status = (args as any)?.status ?? "open";
        const limit = (args as any)?.limit ?? 100;
        return `/api/hourly/positions?status=${status}&limit=${limit}`;
      },
      providesTags: ["HourlyPositions"],
    }),
    getMyHourlyPositions: builder.query<
      any[], { status?: "open" | "closed"; limit?: number } | void
    >({
      query: (args) => {
        const status = (args as any)?.status ?? "open";
        const limit = (args as any)?.limit ?? 100;
        return `/api/me/hourly/positions?status=${status}&limit=${limit}`;
      },
      providesTags: ["MyPositions" as never],
    }),
    getHourlyExitMonitorState: builder.query<any, void>({
      query: () => "/api/hourly/exit-monitor/state",
      providesTags: ["HourlyPositions"],
    }),
    closeHourlyPosition: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/hourly/positions/${id}/close`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["HourlyPositions", "Trades"],
    }),
    dcaHourlyPosition: builder.mutation<any, number>({
      query: (id) => ({
        url: `/api/hourly/positions/${id}/dca-now`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["HourlyPositions", "Trades"],
    }),
    getHourlyMetrics: builder.query<ScalpMetrics & {
      auto_pause_enabled?: boolean;
      auto_pause_window?: number;
      auto_pause_tripped?: boolean;
      consecutive_losses?: number;
      paused?: boolean;
    }, number | void>({
      query: (window) =>
        window ? `/api/hourly/metrics?window=${window}` : "/api/hourly/metrics?window=20",
      providesTags: ["HourlyPositions"],
    }),
    reconcileHourlyOrphans: builder.mutation<any, number | void>({
      query: (lookbackHours) => ({
        url: `/api/hourly/reconcile${lookbackHours ? `?lookback_hours=${lookbackHours}` : ""}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["HourlyPositions"],
    }),

    // ---- LLM cost telemetry ----
    getLlmCostSummary: builder.query<any, number | void>({
      query: (days) => `/api/llm/cost-summary?days=${days || 1}`,
      providesTags: ["LlmCost"],
    }),
    getLlmRecentCalls: builder.query<any[], number | void>({
      query: (limit) => `/api/llm/recent-calls?limit=${limit || 50}`,
      providesTags: ["LlmCost"],
    }),

    // ---- portfolio ----
    getPortfolioPnL: builder.query<unknown, void>({
      query: () => "/api/portfolio/pnl",
      providesTags: ["PortfolioPnL"],
    }),
    getPortfolioRealized: builder.query<unknown, void>({
      query: () => "/api/portfolio/realized",
      providesTags: ["PortfolioPnL"],
    }),
    getPortfolioBreakdown: builder.query<PortfolioBreakdown, { min_value_usd?: number } | void>({
      query: (args) => {
        const min = (args as { min_value_usd?: number } | undefined)?.min_value_usd ?? 5;
        return `/api/portfolio/breakdown?min_value_usd=${min}`;
      },
      providesTags: ["PortfolioPnL"],
    }),

    // ---- presets (quant v3) ----

    // ---- ohlcv (Markets > PriceChart) ----
    getOhlcv: builder.query<OhlcvResponse, OhlcvArgs>({
      query: ({ symbol, timeframe = "1m", limit = 200 }) =>
        `/api/ohlcv?symbol=${encodeURIComponent(symbol)}` +
        `&timeframe=${encodeURIComponent(timeframe)}&limit=${limit}`,
      providesTags: (_r, _e, a) => [{ type: "Ohlcv", id: `${a.symbol}|${a.timeframe ?? "1m"}|${a.limit ?? 200}` }],
    }),

    // ---- server logs ----
    getLogs: builder.query<LogsResponse, LogsArgs | void>({
      query: (args) => {
        const tail = args?.tail ?? 200;
        const level = args?.level ?? "";
        const lvl = level ? `&level=${encodeURIComponent(level)}` : "";
        return `/api/logs?tail=${tail}${lvl}`;
      },
      providesTags: ["Logs"],
    }),

    // ---- V5: stat-arb / MPT / intelligence ----
    getLeadLag: builder.query<LeadLagRow[], number | void>({
      query: (limit) => `/api/v5/lead_lag?limit=${limit ?? 200}`,
      providesTags: ["LeadLag"],
    }),
    getPortfolioWeights: builder.query<PortfolioWeightRow[], number | void>({
      query: (limit) => `/api/v5/portfolio_weights?limit=${limit ?? 200}`,
      providesTags: ["PortfolioWeights"],
    }),
    getArbitrageAttempts: builder.query<ArbAttemptRow[], number | void>({
      query: (limit) => `/api/v5/arbitrage/attempts?limit=${limit ?? 200}`,
      providesTags: ["ArbAttempts"],
    }),
    getArbitrageScan: builder.query<ArbScanResponse, void>({
      query: () => `/api/v5/arbitrage/scan`,
    }),
    getNetFlows: builder.query<NetFlowRow[], number | void>({
      query: (limit) => `/api/v5/net_flows?limit=${limit ?? 200}`,
      providesTags: ["NetFlows"],
    }),
    getSentimentScores: builder.query<SentimentRow[], number | void>({
      query: (limit) => `/api/v5/sentiment_scores?limit=${limit ?? 200}`,
      providesTags: ["Sentiment"],
    }),
    portfolioDryRun: builder.mutation<PortfolioDryRunResult, void>({
      query: () => ({
        url: "/api/v5/portfolio/rebalance/dry-run",
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["PortfolioWeights"],
    }),
    portfolioExecute: builder.mutation<PortfolioExecuteResult, void>({
      query: () => ({
        url: "/api/v5/portfolio/rebalance/execute",
        method: "POST",
        body: { confirm: true },
      }),
      invalidatesTags: ["PortfolioWeights", "Trades"],
    }),

    // ---- Audit agent (admin only) ----
    getAuditReports: builder.query<AuditReport[], void>({
      query: () => "/api/audit/reports?limit=50",
      providesTags: ["AuditReports"],
    }),
    getAuditProposals: builder.query<AuditReportWithProposals, number>({
      query: (rid) => `/api/audit/reports/${rid}/proposals`,
      providesTags: (_r, _e, rid) => [
        { type: "AuditProposals" as const, id: rid },
      ],
    }),
    runAudit: builder.mutation<AuditRunResult[], "hourly" | "scalp" | "both">({
      query: (engine) => ({
        url: `/api/audit/run?engine=${engine}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["AuditReports"],
    }),
    applyAuditProposal: builder.mutation<
      { status: string; applied?: Record<string, unknown> },
      number
    >({
      query: (pid) => ({
        url: `/api/audit/proposals/${pid}/apply`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["AuditReports", "AuditProposals", "Settings"],
    }),
    rejectAuditProposal: builder.mutation<{ status: string }, number>({
      query: (pid) => ({
        url: `/api/audit/proposals/${pid}/reject`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["AuditReports", "AuditProposals"],
    }),
    copyAuditProposal: builder.mutation<
      { status: string; file_hint?: string | null; code_prompt?: string | null },
      number
    >({
      query: (pid) => ({
        url: `/api/audit/proposals/${pid}/copy`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["AuditReports", "AuditProposals"],
    }),

    // ---- DCA engine (admin only) ----
    getDcaHistory: builder.query<DcaRun[], void>({
      query: () => "/api/dca/history?limit=50",
      providesTags: [{ type: "Trades" as const }],
    }),
    // ---- DCA aggregated holdings (admin: all users; user: own) ----
    // Returns avg-buy cost basis + live unrealized P&L per symbol.
    // Backend = /api/dca/holdings.
    getDcaHoldings: builder.query<DcaHoldingsResponse, void>({
      query: () => "/api/dca/holdings",
      providesTags: [{ type: "Trades" as const }],
    }),
    runDcaNow: builder.mutation<DcaTickResult, void>({
      query: () => ({ url: "/api/dca/run-now", method: "POST", body: {} }),
      invalidatesTags: [{ type: "Trades" as const }],
    }),
    // ---- Engine governor ----
    getGovernorStatus: builder.query<GovernorStatus, void>({
      query: () => "/api/governor/status",
    }),
    getGovernorLog: builder.query<GovernorLogRow[], number | void>({
      query: (limit) => `/api/governor/log?limit=${limit ?? 20}`,
    }),
    tickGovernorNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/governor/tick-now", method: "POST", body: {} }),
    }),
    // ---- Contrarian sentiment engine ----
    getContrarianSignalLatest: builder.query<any, { symbol?: string; limit?: number } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        q.set("symbol", a.symbol ?? "BTC/USDT");
        q.set("limit", String(a.limit ?? 1));
        return `/api/contrarian/signal/latest?${q.toString()}`;
      },
    }),
    // ---- Dip-DCA ----
    getDipDcaState: builder.query<any, void>({
      query: () => "/api/dip-dca/state",
    }),
    getDipDcaRecentTicks: builder.query<any, { limit?: number; symbol?: string } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        q.set("limit", String(a.limit ?? 100));
        if (a.symbol) q.set("symbol", a.symbol);
        return `/api/dip-dca/recent-ticks?${q.toString()}`;
      },
    }),
    dipDcaTickNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/dip-dca/tick-now", method: "POST", body: {} }),
    }),
    // ---- Inventory swing engine ----
    getInventorySwingStatus: builder.query<any, void>({
      query: () => "/api/inventory-swing/status",
    }),
    inventorySwingTickNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/inventory-swing/tick-now", method: "POST", body: {} }),
    }),
    inventorySwingSelfTuneNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/inventory-swing/self-tune-now", method: "POST", body: {} }),
    }),
    contrarianRunNow: builder.mutation<any, { symbol?: string; timeframe?: string } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        if (a.symbol) q.set("symbol", a.symbol);
        if (a.timeframe) q.set("timeframe", a.timeframe);
        return { url: `/api/contrarian/run-now?${q.toString()}`,
                 method: "POST", body: {} };
      },
    }),
    // ---- BTC liquidation heatmap ----
    getLiquidationsStatus: builder.query<any, void>({
      query: () => "/api/liquidations/status",
    }),
    getLiquidationsClusters: builder.query<any, { lookback_hours?: number; bucket_pct?: number } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        q.set("lookback_hours", String(a.lookback_hours ?? 24));
        q.set("bucket_pct", String(a.bucket_pct ?? 0.5));
        return `/api/liquidations/clusters?${q.toString()}`;
      },
    }),
    getLiquidationsForecast: builder.query<any, { move_pct: number; lookback_hours?: number }>({
      query: (args) => {
        const q = new URLSearchParams();
        q.set("move_pct", String(args.move_pct));
        q.set("lookback_hours", String(args.lookback_hours ?? 168));
        return `/api/liquidations/forecast?${q.toString()}`;
      },
    }),
    // ---- Pre-pump pattern detector ----
    getPrePumpCandidates: builder.query<PrePumpRow[], { limit?: number; min_score?: number } | number | void>({
      query: (arg) => {
        // Back-compat: accept plain limit number, or {limit, min_score} object.
        const a = (typeof arg === "number" ? { limit: arg } : (arg || {})) as any;
        const limit = a.limit ?? 20;
        const minScore = a.min_score ?? 70;
        return `/api/intelligence/pre-pump?limit=${limit}&min_score=${minScore}`;
      },
    }),
    scanPrePumpNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/intelligence/pre-pump/scan-now", method: "POST", body: {} }),
    }),
    getPrePumpLearnedPattern: builder.query<any, number | void>({
      query: (limit) => `/api/intelligence/pre-pump/learned-pattern?limit=${limit ?? 5}`,
    }),
    learnPrePumpPatternNow: builder.mutation<any, { lookback_days?: number; top_n?: number } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        if (a.lookback_days) q.set("lookback_days", String(a.lookback_days));
        if (a.top_n) q.set("top_n", String(a.top_n));
        return { url: `/api/intelligence/pre-pump/learn-now?${q.toString()}`,
                 method: "POST", body: {} };
      },
    }),
    getPrePumpTrades: builder.query<any[], string | void>({
      query: (status) => `/api/pre-pump/trades${status ? `?status=${status}` : ""}`,
    }),
    getPrePumpSummary: builder.query<any, void>({
      query: () => "/api/pre-pump/summary",
    }),
    runPrePumpBacktest: builder.mutation<any, any>({
      query: (body) => ({ url: "/api/pre-pump/backtest/run", method: "POST", body: body || {} }),
    }),
    getPrePumpBacktestRuns: builder.query<any[], number | void>({
      query: (limit) => `/api/pre-pump/backtest/runs?limit=${limit ?? 20}`,
    }),
    getPrePumpBacktestRun: builder.query<any, number>({
      query: (id) => `/api/pre-pump/backtest/run/${id}`,
    }),
    getPrePumpWatchlist: builder.query<any[], string | void>({
      query: (status) => `/api/pre-pump/watchlist?status=${status ?? "watching"}&limit=50`,
    }),
    tickPrePumpWatchlistNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/pre-pump/watchlist/tick-now", method: "POST", body: {} }),
    }),
    manualPrePumpBuy: builder.mutation<any, { symbol: string; size_usd?: number }>({
      query: (body) => ({
        url: "/api/pre-pump/manual-buy",
        method: "POST",
        body: { symbol: body.symbol, size_usd: body.size_usd ?? 100 },
      }),
    }),
    deepResearch: builder.mutation<any, { symbol: string; use_cache?: boolean }>({
      query: (body) => ({
        url: "/api/pre-pump/research",
        method: "POST",
        body: { symbol: body.symbol, use_cache: body.use_cache ?? true },
      }),
    }),
    getAlphaBscTokens: builder.query<any[], { status?: string; chain?: string; min_score?: number } | string | void>({
      query: (arg) => {
        // Back-compat: accept either a plain status string or an object.
        const a = (typeof arg === "string" ? { status: arg } : (arg || {})) as any;
        const q = new URLSearchParams();
        if (a.status) q.set("status", a.status);
        if (a.chain) q.set("chain", a.chain);
        if (a.min_score != null) q.set("min_score", String(a.min_score));
        const qs = q.toString();
        return `/api/alpha-bsc/tokens${qs ? `?${qs}` : ""}`;
      },
    }),
    discoverAlphaBscByChain: builder.mutation<any, { chain: string }>({
      query: (body) => ({
        url: `/api/alpha-bsc/discover-now?chain=${encodeURIComponent(body.chain)}`,
        method: "POST", body: {},
      }),
    }),
    discoverAlphaAllChains: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/discover-all-chains", method: "POST", body: {} }),
    }),
    trackAlphaBscToken: builder.mutation<any, { symbol_or_address: string; notes?: string }>({
      query: (body) => ({ url: "/api/alpha-bsc/track", method: "POST", body }),
    }),
    refreshAlphaBsc: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/refresh-all", method: "POST", body: {} }),
    }),
    getAlphaBscWsStatus: builder.query<any, void>({
      query: () => "/api/alpha-bsc/ws/status",
    }),
    getAlphaBscWsAlerts: builder.query<any, number | void>({
      query: (limit) => `/api/alpha-bsc/ws/alerts?limit=${limit ?? 30}`,
    }),
    getSpotWsStatus: builder.query<any, void>({
      query: () => "/api/spot-ws/status",
    }),
    getSpotUserDataEvents: builder.query<any, { limit?: number; event_type?: string } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const limit = a.limit ?? 50;
        const et = a.event_type ?? "";
        return `/api/spot-ws/user-data-events?limit=${limit}&event_type=${encodeURIComponent(et)}`;
      },
    }),
    getWalletMovements: builder.query<any, { direction?: string; coin?: string; limit?: number } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        if (a.direction) q.set("direction", a.direction);
        if (a.coin) q.set("coin", a.coin);
        q.set("limit", String(a.limit ?? 100));
        return `/api/wallet/movements?${q.toString()}`;
      },
    }),
    syncWalletMovements: builder.mutation<any, { lookback_days?: number; coin?: string } | void>({
      query: (body) => ({
        url: "/api/wallet/sync", method: "POST",
        body: body || {},
      }),
    }),
    getConvertRecent: builder.query<any, { limit?: number; source?: string } | void>({
      query: (args) => {
        const a = (args || {}) as any;
        const q = new URLSearchParams();
        q.set("limit", String(a.limit ?? 30));
        if (a.source) q.set("source", a.source);
        return `/api/convert/recent?${q.toString()}`;
      },
    }),
    convertExecute: builder.mutation<any, { asset: string; amount: number; max_acceptable_loss_pct?: number }>({
      query: (body) => ({ url: "/api/convert/execute", method: "POST", body }),
    }),
    alphaBscConvertFallback: builder.mutation<any, { position_id: number; force?: boolean; max_acceptable_loss_pct?: number }>({
      query: (body) => ({
        url: `/api/alpha-bsc/positions/${body.position_id}/convert-fallback`,
        method: "POST",
        body: { force: body.force ?? false, max_acceptable_loss_pct: body.max_acceptable_loss_pct },
      }),
    }),
    rankAlphaBscGraduation: builder.mutation<any, number>({
      query: (token_id) => ({
        url: `/api/alpha-bsc/tokens/${token_id}/rank-graduation`,
        method: "POST", body: {},
      }),
    }),
    rankAllAlphaBscGraduation: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/rank-graduation-all", method: "POST", body: {} }),
    }),
    discoverAlphaBscNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/discover-now", method: "POST", body: {} }),
    }),
    getAlphaGeminiDiscoverLatest: builder.query<any, void>({
      query: () => "/api/alpha-bsc/gemini-discover/latest",
      providesTags: ["AlphaGeminiDiscover" as any],
    }),
    alphaGeminiDiscoverRunNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/gemini-discover/run-now", method: "POST", body: {} }),
      invalidatesTags: ["AlphaGeminiDiscover" as any, "AlphaBscTokens" as any],
    }),
    refreshAlphaBscLive: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/refresh-live", method: "POST", body: {} }),
    }),
    alphaBscDeepResearch: builder.mutation<any, { token_id: number; use_cache?: boolean }>({
      query: (body) => ({
        url: "/api/alpha-bsc/research", method: "POST",
        body: { token_id: body.token_id, use_cache: body.use_cache ?? true },
      }),
    }),
    // Live per-step progress for an in-flight deep-research job. Frontend
    // polls every ~1s while the alphaBscDeepResearch mutation is loading.
    getAlphaBscResearchProgress: builder.query<any, number>({
      query: (token_id) => `/api/alpha-bsc/tokens/${token_id}/research-progress`,
    }),
    removeAlphaBscToken: builder.mutation<any, number>({
      query: (id) => ({ url: `/api/alpha-bsc/tokens/${id}`, method: "DELETE" }),
    }),
    getAlphaBscWallet: builder.query<any, void>({
      query: () => "/api/alpha-bsc/wallet",
    }),
    alphaBscBuy: builder.mutation<any, { token_id: number; size_usdt: number; slippage_bps?: number; force?: boolean }>({
      query: (body) => ({ url: "/api/alpha-bsc/buy", method: "POST", body }),
    }),
    alphaBscSell: builder.mutation<any, { position_id: number; pct_to_sell?: number; slippage_bps?: number }>({
      query: (body) => ({ url: "/api/alpha-bsc/sell", method: "POST", body }),
    }),
    getAlphaBscPositions: builder.query<any[], string | void>({
      query: (status) => `/api/alpha-bsc/positions${status ? `?status=${status}` : ""}`,
    }),
    alphaBscAutoTickNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/alpha-bsc/auto/tick-now", method: "POST", body: {} }),
    }),
    getAlphaBscTokenHistory: builder.query<any[], { token_id: number; limit?: number }>({
      query: ({ token_id, limit }) =>
        `/api/alpha-bsc/tokens/${token_id}/history?limit=${limit ?? 50}`,
    }),
    // ---- Performance dashboard extras ----
    getGeminiGatekeeperStats: builder.query<any, number | void>({
      query: (days) => `/api/gatekeeper/stats?days=${days ?? 7}`,
    }),
    getDcaRecentFills: builder.query<any, { days?: number; limit?: number } | void>({
      query: (p) => `/api/dca/recent-fills?days=${p?.days ?? 7}&limit=${p?.limit ?? 100}`,
    }),
    getDcaUniverseChanges: builder.query<any, number | void>({
      query: (limit) => `/api/dca/universe/changes?limit=${limit ?? 50}`,
    }),
    // ---- Gemini Chat (floating FAB) ----
    geminiChat: builder.mutation<any, { messages: { role: string; content: string }[]; include_context?: boolean; use_search?: boolean }>({
      query: (body) => ({ url: "/api/gemini-chat", method: "POST", body }),
    }),
    // ---- Asset Deep Research ----
    runAssetResearch: builder.mutation<any, { query: string }>({
      query: (body) => ({ url: "/api/asset-research/run", method: "POST", body }),
    }),
    getRecentAssetResearch: builder.query<{ runs: any[] }, number | void>({
      query: (limit) => `/api/asset-research/recent?limit=${limit ?? 30}`,
    }),
    getAssetResearchRun: builder.query<any, number>({
      query: (id) => `/api/asset-research/run/${id}`,
    }),
    // ---- 10× Allocation Policy ----
    getAllocationPlan: builder.query<any, number | void>({
      query: (total) => `/api/allocation/plan${total ? `?total=${total}` : ""}`,
    }),
    applyAllocation: builder.mutation<any, { total?: number; dry_run?: boolean }>({
      query: (body) => ({ url: "/api/allocation/apply", method: "POST", body }),
    }),
    getAllocationPerformance: builder.query<any, number | void>({
      query: (days) => `/api/allocation/performance?days=${days ?? 30}`,
    }),
    // ---- BTC Grid Trader ----
    getGridPresets: builder.query<{ presets: Record<string, any> }, void>({
      query: () => "/api/grid/presets",
    }),
    getGridStatus: builder.query<any, void>({
      query: () => "/api/grid/status",
    }),
    gridTickNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/grid/tick-now", method: "POST", body: {} }),
    }),
    runGridBacktest: builder.mutation<any, { preset: string; days: number; equity: number }>({
      query: (body) => ({ url: "/api/grid/backtest/run", method: "POST", body }),
    }),
    getGridBacktestRuns: builder.query<{ runs: any[] }, number | void>({
      query: (limit) => `/api/grid/backtest/runs?limit=${limit ?? 20}`,
    }),
    getGridSymbolOrders: builder.query<any, { base: string; fills_limit?: number }>({
      query: ({ base, fills_limit }) =>
        `/api/grid/symbol/${encodeURIComponent(base)}/orders?fills_limit=${fills_limit ?? 100}`,
    }),
    getGridSymbolChart: builder.query<any, { base: string; timeframe?: string; limit?: number }>({
      query: ({ base, timeframe, limit }) =>
        `/api/grid/symbol/${encodeURIComponent(base)}/chart?timeframe=${timeframe ?? "4h"}&limit=${limit ?? 80}`,
    }),
    getConvictionDcaPicks: builder.query<any[], string | void>({
      query: (status) => `/api/conviction-dca/picks${status ? `?status=${status}` : ""}`,
    }),
    runConvictionDcaSelection: builder.mutation<any, void>({
      query: () => ({ url: "/api/conviction-dca/selection-now", method: "POST", body: {} }),
    }),
    tickConvictionDcaNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/conviction-dca/tick-now", method: "POST", body: {} }),
    }),
    // ---- Strategy library + advisor ----
    getStrategyLibrary: builder.query<StrategyLibrarySummary, void>({
      query: () => "/api/strategy/library",
    }),
    getStrategyDetail: builder.query<StrategySpec, string>({
      query: (id) => `/api/strategy/library/${id}`,
    }),
    consultStrategy: builder.mutation<StrategyConsultResult,
                                       { symbol: string; n_candidates?: number }>({
      query: (body) => ({ url: "/api/strategy/consult", method: "POST", body }),
    }),
    getStrategyConsults: builder.query<StrategyConsultRow[], number | void>({
      query: (limit) => `/api/strategy/consults?limit=${limit ?? 30}`,
    }),
    // ---- Library-driven autonomous engine ----
    runLibraryEngineNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/library-engine/run-now", method: "POST", body: {} }),
      invalidatesTags: [{ type: "Trades" as const }],
    }),
    getLibraryEngineRuns: builder.query<any[], number | void>({
      query: (limit) => `/api/library-engine/runs?limit=${limit ?? 20}`,
    }),
    // ---- Smart DCA (Gemini-advised dip-buying layer) ----
    smartDcaRunNow: builder.mutation<any, void>({
      query: () => ({ url: "/api/dca/smart-run-now", method: "POST", body: {} }),
      invalidatesTags: [{ type: "Trades" as const }],
    }),
    // ---- PAXG rebalance (separate from smart DCA — explicit consent) ----
    paxgConsult: builder.mutation<PaxgConsultResponse, void>({
      query: () => ({ url: "/api/paxg/rebalance/consult", method: "POST", body: {} }),
    }),
    paxgApply: builder.mutation<any, { percent: number }>({
      query: (body) => ({ url: "/api/paxg/rebalance/apply", method: "POST", body }),
      invalidatesTags: [{ type: "Trades" as const }],
    }),
    sendNotificationsTest: builder.mutation<
      { status: string; sent_to?: string[]; reason?: string; failed?: any[] },
      { recipient?: string } | void
    >({
      query: (args) => {
        const q = (args as any)?.recipient
          ? `?recipient=${encodeURIComponent((args as any).recipient)}`
          : "";
        return { url: `/api/notifications/test${q}`, method: "POST", body: {} };
      },
    }),

    // ---- Regime overlay (any logged-in user can see state) ----
    getRegimeCurrent: builder.query<{
      state: "risk_on" | "neutral" | "risk_off";
      cached_age_seconds: number | null;
      reasons: Array<{ signal: string; verdict?: string; [k: string]: unknown }>;
      last_flip_unix: number | null;
      overlay_enabled: boolean;
      forced_state: "auto" | "risk_on" | "neutral" | "risk_off";
    }, void>({
      query: () => "/api/regime/current",
    }),

    // ---- Admin per-user dashboard (admin only) ----
    getAdminUserHourlyPositions: builder.query<
      any[], { uid: number; status?: "open" | "closed"; limit?: number }
    >({
      query: ({ uid, status = "open", limit = 200 }) =>
        `/api/admin/users/${uid}/hourly-positions?status=${status}&limit=${limit}`,
      providesTags: ["HourlyPositions"],
    }),
    getAdminUserTrades: builder.query<
      BackendTrade[], { uid: number; limit?: number }
    >({
      query: ({ uid, limit = 100 }) =>
        `/api/admin/users/${uid}/trades?limit=${limit}`,
      providesTags: ["Trades"],
    }),
    getAdminUserLlmCost: builder.query<
      {
        days: number; total_calls: number; total_tokens: number;
        total_cost_usd: number;
        by_agent: { agent: string; calls: number; tokens: number;
                     cost_usd: number; avg_latency_ms: number; errors: number }[];
      },
      { uid: number; days?: number }
    >({
      query: ({ uid, days = 7 }) =>
        `/api/admin/users/${uid}/llm-cost?days=${days}`,
      providesTags: ["LlmCost"],
    }),

    // ---- News watcher (admin only) ----
    getNewsEvents: builder.query<NewsEvent[], { status?: string; min_severity?: string } | void>({
      query: (args) => {
        const p = new URLSearchParams({ limit: "100" });
        if (args && (args as any).status) p.set("status", (args as any).status);
        if (args && (args as any).min_severity) p.set("min_severity", (args as any).min_severity);
        return `/api/news/events?${p.toString()}`;
      },
      providesTags: ["NewsEvents"],
    }),
    pollNewsNow: builder.mutation<Record<string, unknown>, void>({
      query: () => ({ url: "/api/news/poll", method: "POST", body: {} }),
      invalidatesTags: ["NewsEvents"],
    }),
    dismissNewsEvent: builder.mutation<{ status: string }, number>({
      query: (eid) => ({ url: `/api/news/events/${eid}/dismiss`, method: "POST", body: {} }),
      invalidatesTags: ["NewsEvents"],
    }),
    sellAffectedForNews: builder.mutation<
      { closed: any[]; failed: any[] }, number
    >({
      query: (eid) => ({ url: `/api/news/events/${eid}/sell-affected`, method: "POST", body: {} }),
      invalidatesTags: ["NewsEvents", "HourlyPositions", "Trades"],
    }),
  }),
});

// ---- DCA types ----
export interface DcaRun {
  id: number;
  ts: string;
  user_id: number | null;
  user_email?: string | null;
  status: "ok" | "skipped" | "error";
  symbols_json: string | null;
  amount_usd_per_run: number | null;
  fills_json: string | null;
  note: string | null;
  // Enriched at request time with live ticker prices.
  run_cost_usd?: number | null;
  run_current_value_usd?: number | null;
  run_unrealized_pnl_usd?: number | null;
}

export interface DcaHolding {
  symbol: string;
  qty: number;
  cost_usd: number;
  avg_price: number;
  current_price: number | null;
  unrealized_usd: number | null;
  unrealized_pct: number | null;
  realized_usd: number;
  qty_sold_lifetime: number;
  proceeds_lifetime_usd: number;
}

export interface DcaHoldingsResponse {
  holdings: DcaHolding[];
  totals: {
    cost_usd: number;
    value_usd: number;
    unrealized_usd: number;
    unrealized_pct: number;
    realized_usd: number;
    total_pnl_usd: number;
    compound_pool_usd: number;
  };
}

export interface StrategyLibrarySummary {
  total: number;
  schema_version: number;
  last_updated: string;
  by_category: Record<string, number>;
  by_regime_fit: Record<string, number>;
  ids: string[];
}

export interface StrategySpec {
  id: string;
  name: string;
  category: string;
  source: string;
  fits_regime: string[];
  avoids_regime: string[];
  min_timeframe: string;
  entry: { conditions: string[]; trigger: string };
  exit: Record<string, any>;
  sizing: Record<string, any>;
  expected_win_rate: number;
  rr_ratio: number;
  notes: string;
}

export interface StrategyConsultResult {
  status: "ok" | "error" | "no_candidates";
  reason?: string;
  model?: string;
  symbol?: string;
  conditions?: Record<string, any>;
  candidates_considered?: Array<{ id: string; name: string; score: number; category: string }>;
  verdict?: {
    decision: "use_strategy" | "skip";
    strategy_id?: string | null;
    rationale: string;
    confidence: number;
    parameters?: {
      action: "BUY" | "HOLD";
      entry_price?: number;
      stop_price?: number;
      target_price?: number;
      size_pct_equity?: number;
    };
  };
}

export interface StrategyConsultRow {
  id: number;
  ts: string;
  symbol: string;
  conditions_json: string;
  candidate_ids: string;
  decision: string;
  strategy_id?: string | null;
  rationale: string;
  confidence: number;
  parameters_json: string;
  model: string;
}

export interface PaxgConsultResponse {
  status: "ok" | "error";
  reason?: string;
  model?: string;
  snapshot?: {
    paxg_holding_qty: number;
    paxg_holding_usd: number;
    paxg_price: number;
    usdt_holding_usd: number;
    crypto_exposure_usd: number;
    usdt_pct_of_crypto: number | null;
    btc_now_usd?: number;
    btc_24h_change_pct?: number;
    btc_7d_change_pct?: number;
    btc_dist_sma50_pct?: number;
    btc_rsi_daily?: number;
    btc_rsi_4h?: number;
    auto_defense_engaged: boolean;
    market_regime: string;
    dca_position?: Array<{ symbol: string; avg_buy: number; current: number; pct_below_avg: number }>;
  };
  verdict?: {
    action: "hold" | "rebalance";
    percent_to_move: number;
    target_war_chest_usd: number;
    confidence: number;
    rationale: string;
    risk_warnings: string[];
  };
}

export interface PrePumpRow {
  id: number;
  ts: string;
  symbol: string;
  change_24h_pct: number;
  rsi_daily: number;
  drift_7d_pct: number;
  dist_from_sma20_pct: number;
  vol_expansion_ratio: number;
  avg_range_pct: number;
  pattern_match: string;     // A_oversold_bounce | B_momentum_continuation | both | none
  pattern_score: number;
  note?: string;
}

export interface GovernorBreakdown {
  signal: string;
  delta?: number;
  tag?: string;
  value?: number | string;
  error?: string;
  btc?: number;
  sma50?: number;
  diff_pct?: number;
  n_trades?: number;
  engaged?: boolean;
}

export interface GovernorStatus {
  score: number;
  regime: "favorable" | "neutral" | "hostile";
  breakdown: GovernorBreakdown[];
  btc_above_sma50?: boolean | null;
  defense_engaged?: boolean;
}

export interface GovernorLogRow {
  id: number;
  ts: string;
  score: number;
  regime: string;
  action: string;       // arm | pause | hold | hysteresis_block
  engines_before: string;
  engines_after?: string | null;
  note?: string | null;
  assessment_json?: string;
}

export interface DcaTickResult {
  status: string;
  reason?: string;
  user_id?: number;
  amount_usd_per_run?: number;
  fills?: Array<{
    symbol: string;
    status: string;
    reason?: string;
    size_usd?: number;
    filled_qty?: number;
    avg_price?: number;
    order_id?: string;
  }>;
}

// ---- News-watcher types ----
export interface NewsEvent {
  id: number;
  detected_at: string;
  published_at: string | null;
  source: "binance" | "coindesk" | "theblock" | "decrypt" | "rss";
  category: "delisting" | "listing" | "monitoring" | "news" | "risk";
  severity: "low" | "medium" | "high" | "critical";
  symbols_json: string;   // JSON list of bare symbols
  title: string;
  url: string | null;
  summary: string | null;
  fingerprint: string;
  actions_taken: string | null;
  status: "open" | "dismissed" | "resolved";
}

// ---- Audit-agent types ----
export interface AuditReport {
  id: number;
  engine: "hourly" | "scalp";
  created_at: string;
  trigger: "manual" | "daily";
  model: string | null;
  summary: string | null;
  status: "ok" | "error";
  pending_count: number;
  total_count: number;
}

export interface AuditProposal {
  id: number;
  report_id: number;
  kind: "setting" | "code";
  title: string;
  rationale: string;
  patch_json: string | null;
  file_hint: string | null;
  code_prompt: string | null;
  status: "pending" | "applied" | "rejected" | "copied";
  decided_at: string | null;
  decided_by: string | null;
  note: string | null;
}

export interface AuditReportWithProposals {
  report: AuditReport & { raw_response?: string; context_json?: string };
  proposals: AuditProposal[];
}

export interface AuditRunResult {
  report_id?: number;
  engine: string;
  trigger?: string;
  summary?: string;
  proposals_count?: number;
  model?: string;
  status: string;
  error?: string;
}

export interface OhlcvArgs {
  symbol: string;
  timeframe?: string;
  limit?: number;
}
export interface OhlcvResponse {
  symbol: string;
  timeframe: string;
  /** Each row is [ts_ms, open, high, low, close, volume]. */
  rows: number[][];
  cached?: boolean;
}

export interface LogsArgs {
  tail?: number;
  level?: string;
}
export interface LogsResponse {
  path: string;
  exists: boolean;
  size_bytes?: number;
  level_filter?: string | null;
  lines: string[];
}

// ---- V5 row types ---------------------------------------------------------

export interface LeadLagRow {
  id: number;
  computed_at: string;
  leader: string;
  follower: string;
  lag_seconds: number;
  correlation: number;
  n_obs: number;
  window_min: number;
}

export interface PortfolioWeightRow {
  id: number;
  computed_at: string;
  asset: string;
  weight: number;
  reason: string | null;
  source_run: string | null;
}

export interface ArbAttemptRow {
  id: number;
  started_at: string;
  finished_at: string | null;
  triangle_json: string;
  quoted_edge_pct: number;
  realized_edge_pct: number | null;
  status: string;
  realized_pnl_usd: number | null;
  notes: string | null;
  dry_run: number;
}

export interface ArbScanRow {
  triangle: string;
  leg1_market: string;
  leg2_market: string;
  leg3_market: string;
  edge_after_fees_pct: number | null;
  edge_after_fees_and_spread_pct: number | null;
  book_depth_quote_usd: number | null;
  passed: boolean;
  error: string | null;
}
export interface ArbScanResponse {
  scanned_at: number;
  rows: ArbScanRow[];
  count: number;
  passed_count: number;
}

export interface NetFlowRow {
  id: number;
  fetched_at: string;
  asset: string;
  window_hours: number;
  inflow_usd: number | null;
  outflow_usd: number | null;
  net_inflow_usd: number | null;
  source: string | null;
}

export interface SentimentRow {
  id: number;
  computed_at: string;
  symbol: string;
  score: number;
  n_items: number;
  window_hours: number;
  top_items_json: string | null;
}

export interface PortfolioDryRunLeg {
  asset: string;
  current_weight: number;
  target_weight: number;
  drift: number;
  direction: "BUY" | "SELL";
  size_usd: number;
}

export interface PortfolioDryRunResult {
  target_weights: Record<string, number>;
  reason: string;
  computed_at: string;
  current_value_usd: Record<string, number>;
  rebalance_triggered: boolean;
  max_drift_pct: number;
  legs: PortfolioDryRunLeg[];
}

export interface PortfolioExecuteLeg {
  asset: string;
  direction: string;
  requested_usd: number;
  status: string;       // executed | skipped | error
  reason?: string;
  executed_usd?: number | null;
  order_id?: string | null;
}
export interface PortfolioExecuteResult {
  triggered: boolean;
  dry_run?: boolean;
  summary: string;
  max_drift_pct: number;
  executed: PortfolioExecuteLeg[];
  skipped: PortfolioExecuteLeg[];
  errors: PortfolioExecuteLeg[];
}

// ---- portfolio breakdown (Holdings route) ----
export type BuyDistanceBucket = "imminent" | "near" | "deep" | "parked";

export interface PortfolioBreakdownSummary {
  total_portfolio_usd: number;
  usdt_total: number;
  usdt_free: number;
  usdt_locked: number;
  holdings_usd: number;
  open_buy_usd: number;
  open_sell_usd: number;
  buy_distance_buckets: Record<BuyDistanceBucket, number>;
}

export interface PortfolioAsset {
  asset: string;
  qty: number;
  price: number;
  value_usd: number;
  free_qty: number;
  locked_qty: number;
  pct: number;
}

export interface PortfolioOpenOrder {
  symbol: string;
  side: "buy" | "sell";
  price: number;
  qty: number;
  notional_usd: number;
  market: number;
  distance_pct: number;
  bucket: BuyDistanceBucket;
}

export interface PortfolioBreakdown {
  summary: PortfolioBreakdownSummary;
  assets: PortfolioAsset[];
  orders: PortfolioOpenOrder[];
}

export const {
  useGetHealthQuery,
  useLoginMutation,
  useSignupMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useResendVerificationMutation,
  useGetTermsQuery,
  useGetAuthMeQuery,
  useGetMyStatusQuery,
  useGetMySettingsQuery,
  useGetMyMicroHarvestQuery,
  usePatchMySettingsMutation,
  usePutMyBinanceKeysMutation,
  useChangeMyPasswordMutation,
  useDeleteMyBinanceKeysMutation,
  // Multi-user scalp section
  useGetBtcDominanceQuery,
  useGetWalletReconcileQuery,
  useWalletReconcileSellMutation,
  useHourlyAttachOcoMutation,
  useGetMyPendingQuery,
  useApproveMyPendingMutation,
  useRejectMyPendingMutation,
  useGetMyTradesQuery,
  useGetMyPositionsQuery,
  useGetPublicCyclesStatsQuery,
  useGetMyCyclesQuery,
  useGetMyCycleDetailQuery,
  useGetMyHourlyMetricsQuery,
  useRunMyCycleMutation,
  useGetMyROICurveQuery,
  useGetMyFeesQuery,
  useGetAdminUsersQuery,
  useGetAdminUserDetailQuery,
  usePatchAdminUserSettingsMutation,
  useRefanoutAdminUserMutation,
  useLoginAsAdminUserMutation,
  useSetAdminUserStatusMutation,
  useGetAdminFeesQuery,
  useMarkAdminFeePaidMutation,
  useMarkAdminFeePeriodPaidMutation,
  useGetStatusQuery,
  useGetSettingsQuery,
  usePatchSettingsMutation,
  useReloadSettingsMutation,
  useRunCycleMutation,
  useRunCycleAsyncMutation,
  useGetCyclesQuery,
  useGetCycleStepsQuery,
  useStartBotMutation,
  useStopBotMutation,
  useGetDecisionsQuery,
  useGetTradesQuery,
  useGetPendingQuery,
  useApprovePendingMutation,
  useRejectPendingMutation,
  useCancelAllPendingMutation,
  useGetGatekeeperStatsQuery,
  useGetGatekeeperRecentQuery,
  useRunTunerNowMutation,
  useGetTuningRecentQuery,
  useGetWatchlistCurrentQuery,
  useGetWatchlistRecentQuery,
  useRunWatchlistNowMutation,
  useGetNarrativeCurrentQuery,
  useGetNarrativeRecentQuery,
  useRunNarrativeNowMutation,
  useGetPostTradeReviewQuery,
  useGetPostTradeReviewsRecentQuery,
  useGetLlmCostSummaryQuery,
  useGetLlmRecentCallsQuery,
  useGetSessionPlanCurrentQuery,
  useGetSessionPlanRecentQuery,
  useRunSessionPlanNowMutation,
  useGetControllerStateQuery,
  useGetHourlyPositionsQuery,
  useGetMyHourlyPositionsQuery,
  useGetHourlyExitMonitorStateQuery,
  useCloseHourlyPositionMutation,
  useDcaHourlyPositionMutation,
  useGetHourlyMetricsQuery,
  useReconcileHourlyOrphansMutation,
  useGetPortfolioPnLQuery,
  useGetPortfolioRealizedQuery,
  useGetPortfolioBreakdownQuery,
  useGetOhlcvQuery,
  useGetLogsQuery,
  // V5
  useGetLeadLagQuery,
  useGetPortfolioWeightsQuery,
  useGetArbitrageAttemptsQuery,
  useGetArbitrageScanQuery,
  useGetNetFlowsQuery,
  useGetSentimentScoresQuery,
  usePortfolioDryRunMutation,
  usePortfolioExecuteMutation,
  // Audit agent
  useGetAuditReportsQuery,
  useGetAuditProposalsQuery,
  useRunAuditMutation,
  useApplyAuditProposalMutation,
  useRejectAuditProposalMutation,
  useCopyAuditProposalMutation,
  // News watcher
  useGetNewsEventsQuery,
  usePollNewsNowMutation,
  useDismissNewsEventMutation,
  useSellAffectedForNewsMutation,
  // DCA engine
  useGetDcaHistoryQuery,
  useGetDcaHoldingsQuery,
  useRunDcaNowMutation,
  useGetStrategyLibraryQuery,
  useGetStrategyDetailQuery,
  useConsultStrategyMutation,
  useGetStrategyConsultsQuery,
  useRunLibraryEngineNowMutation,
  useGetLibraryEngineRunsQuery,
  useGetGovernorStatusQuery,
  useGetContrarianSignalLatestQuery,
  useContrarianRunNowMutation,
  useGetDipDcaStateQuery,
  useGetDipDcaRecentTicksQuery,
  useDipDcaTickNowMutation,
  useGetInventorySwingStatusQuery,
  useInventorySwingTickNowMutation,
  useInventorySwingSelfTuneNowMutation,
  useGetLiquidationsStatusQuery,
  useGetLiquidationsClustersQuery,
  useGetLiquidationsForecastQuery,
  useGetGovernorLogQuery,
  useTickGovernorNowMutation,
  useGetPrePumpCandidatesQuery,
  useGetPrePumpLearnedPatternQuery,
  useLearnPrePumpPatternNowMutation,
  useScanPrePumpNowMutation,
  useGetPrePumpTradesQuery,
  useGetPrePumpSummaryQuery,
  useRunPrePumpBacktestMutation,
  useGetPrePumpBacktestRunsQuery,
  useGetPrePumpBacktestRunQuery,
  useGetPrePumpWatchlistQuery,
  useTickPrePumpWatchlistNowMutation,
  useManualPrePumpBuyMutation,
  useDeepResearchMutation,
  useGetConvictionDcaPicksQuery,
  useRunConvictionDcaSelectionMutation,
  useTickConvictionDcaNowMutation,
  useGetAlphaBscTokensQuery,
  useDiscoverAlphaBscByChainMutation,
  useDiscoverAlphaAllChainsMutation,
  useTrackAlphaBscTokenMutation,
  useRefreshAlphaBscMutation,
  useGetAlphaBscWsStatusQuery,
  useGetAlphaBscWsAlertsQuery,
  useGetSpotWsStatusQuery,
  useGetSpotUserDataEventsQuery,
  useGetWalletMovementsQuery,
  useSyncWalletMovementsMutation,
  useGetConvertRecentQuery,
  useConvertExecuteMutation,
  useAlphaBscConvertFallbackMutation,
  useRankAlphaBscGraduationMutation,
  useRankAllAlphaBscGraduationMutation,
  useDiscoverAlphaBscNowMutation,
  useGetAlphaGeminiDiscoverLatestQuery,
  useGetGridClosedCyclesQuery,
  useGetProfitTimeseriesQuery,
  useGetLegacyPnlQuery,
  useAlphaGeminiDiscoverRunNowMutation,
  useRefreshAlphaBscLiveMutation,
  useAlphaBscDeepResearchMutation,
  useGetAlphaBscResearchProgressQuery,
  useRemoveAlphaBscTokenMutation,
  useGetAlphaBscWalletQuery,
  useAlphaBscBuyMutation,
  useAlphaBscSellMutation,
  useGetAlphaBscPositionsQuery,
  useAlphaBscAutoTickNowMutation,
  useGetAlphaBscTokenHistoryQuery,
  useSmartDcaRunNowMutation,
  usePaxgConsultMutation,
  usePaxgApplyMutation,
  useSendNotificationsTestMutation,
  // Regime overlay
  useGetRegimeCurrentQuery,
  // Admin per-user dashboard
  useGetAdminUserHourlyPositionsQuery,
  useGetAdminUserTradesQuery,
  useGetAdminUserLlmCostQuery,
  useGeminiChatMutation,
  useGetGeminiGatekeeperStatsQuery,
  useGetDcaRecentFillsQuery,
  useGetDcaUniverseChangesQuery,
  // Asset research
  useRunAssetResearchMutation,
  useGetRecentAssetResearchQuery,
  useGetAssetResearchRunQuery,
  // Allocation
  useGetAllocationPlanQuery,
  useApplyAllocationMutation,
  useGetAllocationPerformanceQuery,
  // Grid trader
  useGetGridPresetsQuery,
  useGetGridStatusQuery,
  useGridTickNowMutation,
  useRunGridBacktestMutation,
  useGetGridBacktestRunsQuery,
  useGetGridSymbolOrdersQuery,
  useGetGridSymbolChartQuery,
} = api;
